import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface MatrixPayload {
  href: string;
  title?: string;
}

interface MatrixState extends MatrixPayload {
  active: boolean;
  runId: number;
}

interface RainColumn {
  x: number;
  y: number;
  speed: number;
  trail: number;
}

const MATRIX_EVENT = 'cinevault:matrix-navigate';
const TRANSITION_DURATION_MS = 920;
const NAVIGATE_DELAY_MS = 620;
const MATRIX_GLYPHS = 'ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

declare global {
  interface WindowEventMap {
    [MATRIX_EVENT]: CustomEvent<MatrixPayload>;
  }
}

export function launchMatrixTransition(payload: MatrixPayload) {
  window.dispatchEvent(new CustomEvent(MATRIX_EVENT, { detail: payload }));
}

function randomGlyph() {
  return MATRIX_GLYPHS[Math.floor(Math.random() * MATRIX_GLYPHS.length)];
}

function createColumns(width: number, height: number, fontSize: number): RainColumn[] {
  const columnGap = fontSize * 0.78;
  const count = Math.ceil(width / columnGap);
  return Array.from({ length: count }, (_, index) => ({
    x: index * columnGap + Math.random() * 4,
    y: Math.random() * (height + fontSize * 14),
    speed: fontSize * (1.08 + Math.random() * 1.85),
    trail: 12 + Math.floor(Math.random() * 26),
  }));
}

export default function MatrixRainTransition() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}${location.hash}`;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const matrixNavigationPendingRef = useRef(false);
  const routeKeyRef = useRef(routeKey);
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationRef = useRef<number | null>(null);
  const [matrix, setMatrix] = useState<MatrixState>({ active: false, href: '', runId: 0 });

  const clearTransitionTimers = () => {
    if (navigateTimeoutRef.current) {
      clearTimeout(navigateTimeoutRef.current);
      navigateTimeoutRef.current = null;
    }
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleMatrix = (event: WindowEventMap[typeof MATRIX_EVENT]) => {
      const { href, title } = event.detail;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      clearTransitionTimers();

      if (prefersReducedMotion) {
        navigate(href);
        return;
      }

      setMatrix((current) => ({
        active: true,
        href,
        title,
        runId: current.runId + 1,
      }));
      matrixNavigationPendingRef.current = true;
      navigateTimeoutRef.current = setTimeout(() => {
        navigate(href);
      }, NAVIGATE_DELAY_MS);
      exitTimeoutRef.current = setTimeout(() => {
        matrixNavigationPendingRef.current = false;
        setMatrix((current) => ({ ...current, active: false }));
        exitTimeoutRef.current = null;
      }, TRANSITION_DURATION_MS);
    };

    window.addEventListener(MATRIX_EVENT, handleMatrix);
    return () => {
      window.removeEventListener(MATRIX_EVENT, handleMatrix);
      clearTransitionTimers();
    };
  }, [navigate]);

  useEffect(() => {
    if (routeKeyRef.current === routeKey) return;

    routeKeyRef.current = routeKey;

    if (matrixNavigationPendingRef.current) {
      matrixNavigationPendingRef.current = false;
      return;
    }

    // Browser back, Home, or any non-Matrix route change should not leave the
    // transition controller half-active for the next title click.
    clearTransitionTimers();
    setMatrix((current) => (current.active ? { ...current, active: false } : current));
  }, [routeKey]);

  useEffect(() => {
    if (!matrix.active || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const fontSize = Math.max(14, Math.round(window.innerWidth / 92));
    let columns: RainColumn[] = [];
    let lastTime = performance.now();

    const resize = () => {
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = createColumns(window.innerWidth, window.innerHeight, fontSize);
    };

    const drawGlyph = (glyph: string, x: number, y: number, alpha: number, isHead: boolean) => {
      ctx.fillStyle = isHead
        ? `rgba(232, 255, 232, ${alpha})`
        : `rgba(50, 255, 82, ${alpha})`;
      ctx.shadowColor = isHead ? '#eaffea' : '#00ff41';
      ctx.shadowBlur = isHead ? 16 : 7;
      ctx.fillText(glyph, x, y);
    };

    const drawFrame = (delta: number) => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.font = `700 ${fontSize}px 'Courier New', monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      for (const column of columns) {
        column.y += column.speed * delta;

        for (let i = 0; i < column.trail; i++) {
          const y = column.y - i * fontSize;
          if (y < -fontSize || y > window.innerHeight + fontSize) continue;

          const alpha = Math.max(0, 1 - i / column.trail);
          drawGlyph(randomGlyph(), column.x, y, alpha, i === 0);
        }

        if (column.y - column.trail * fontSize > window.innerHeight) {
          column.y = Math.random() * -window.innerHeight * 0.35;
          column.speed = fontSize * (1 + Math.random() * 1.9);
          column.trail = 12 + Math.floor(Math.random() * 26);
        }
      }
    };

    const tick = (time: number) => {
      const delta = Math.min(32, time - lastTime) / 16.67;
      lastTime = time;

      drawFrame(delta);
      animationRef.current = requestAnimationFrame(tick);
    };

    resize();
    drawFrame(1);
    window.addEventListener('resize', resize);
    animationRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    };
  }, [matrix.active, matrix.runId]);

  if (!matrix.active) return null;

  return (
    <div key={matrix.runId} className="matrix-transition" aria-hidden="true">
      <canvas ref={canvasRef} className="matrix-transition__canvas" />
      <div className="matrix-transition__vignette" />
    </div>
  );
}
